import React, { useState, useEffect, useCallback } from 'react';
import { Card, Input, Switch, Select, Button, message, Space, Row, Col, Divider, Typography, ColorPicker } from 'antd';
import { apiFetch } from '../utils/api.js';

const TAB_DEFS = [
  { key: 'home', label: '首页' },
  { key: 'inventory', label: '库存' },
  { key: 'purchase', label: '采购' },
  { key: 'products', label: '成本' },
  { key: 'aftersales', label: '售后' },
];
const MODULE_LABELS = {
  dashboard: '数据看板', inbound: '每日入库', inventory: '库存',
  purchase: '采购', products: '商品成本', aftersales: '售后',
};
const HOME_OPTS = [
  { value: 'dashboard', label: '数据看板' },
  { value: 'inbound', label: '每日入库' },
];

export default function MpConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiFetch('/api/mp-config').then(r => r.json()).then(d => setCfg(d.data || {})).catch(() => setCfg({}));
  }, []);

  useEffect(() => { load(); }, [load]);

  // 深拷贝 + 按路径更新
  const up = (path, value) => {
    setCfg(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const keys = path.split('.');
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (o[keys[i]] == null || typeof o[keys[i]] !== 'object') o[keys[i]] = {};
        o = o[keys[i]];
      }
      o[keys[keys.length - 1]] = value;
      return next;
    });
  };
  const upTab = (key, patch) => {
    setCfg(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const tabs = next.tabs = next.tabs || [];
      let t = tabs.find(x => x.key === key);
      if (!t) { t = { key, name: key, visible: true }; tabs.push(t); }
      Object.assign(t, patch);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const res = await apiFetch('/api/mp-config', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg),
    }).then(r => r.json());
    setSaving(false);
    if (res.success) message.success('已保存，手机端刷新即生效');
    else message.error(res.error || '保存失败');
  };

  if (!cfg) return <div style={{ textAlign: 'center', padding: 48, color: '#8c8c8c' }}>加载中…</div>;

  const tabs = cfg.tabs || [];
  const roles = cfg.roles || {};
  const texts = cfg.texts || {};
  const modules = cfg.modules || {};

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>移动端配置（小程序 H5）</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        在这里调整移动端页面，保存后手机端刷新即生效，<b>无需重新上传微信审核</b>。
      </Typography.Paragraph>

      <Card title="主题配色" size="small" style={{ marginBottom: 16 }}>
        <Row align="middle">
          <Col span={6}>主色（珊瑚色）</Col>
          <Col span={18}>
            <ColorPicker
              value={cfg.theme?.primaryColor || 'var(--brand)'}
              onChange={(c) => up('theme.primaryColor', c.toHexString())}
              showText
            />
          </Col>
        </Row>
      </Card>

      <Card title="底部菜单导航（名称 / 显示）" size="small" style={{ marginBottom: 16 }}>
        {TAB_DEFS.map(def => {
          const t = tabs.find(x => x.key === def.key) || { key: def.key, name: def.label, visible: true };
          return (
            <Row key={def.key} align="middle" style={{ marginBottom: 12 }}>
              <Col span={4} style={{ color: '#8c8c8c' }}>{def.label}</Col>
              <Col span={12}>
                <Input value={t.name} onChange={e => upTab(def.key, { name: e.target.value })} placeholder="名称" />
              </Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                显示 <Switch checked={t.visible !== false} onChange={v => upTab(def.key, { visible: v })} />
              </Col>
            </Row>
          );
        })}
      </Card>

      <Card title="角色权限（首页显示什么）" size="small" style={{ marginBottom: 16 }}>
        <Row align="middle" style={{ marginBottom: 12 }}>
          <Col span={8}>管理员 admin</Col>
          <Col span={16}>
            <Select value={roles.admin?.homeType || 'dashboard'} onChange={v => up('roles.admin.homeType', v)} options={HOME_OPTS} style={{ width: '100%' }} />
          </Col>
        </Row>
        <Row align="middle" style={{ marginBottom: 12 }}>
          <Col span={8}>仓库 warehouse</Col>
          <Col span={16}>
            <Select value={roles.warehouse?.homeType || 'inbound'} onChange={v => up('roles.warehouse.homeType', v)} options={HOME_OPTS} style={{ width: '100%' }} />
          </Col>
        </Row>
        <Row align="middle">
          <Col span={8}>其他 other</Col>
          <Col span={16}>
            <Select value={roles.other?.homeType || 'inbound'} onChange={v => up('roles.other.homeType', v)} options={HOME_OPTS} style={{ width: '100%' }} />
          </Col>
        </Row>
      </Card>

      <Card title="文案（登录页）" size="small" style={{ marginBottom: 16 }}>
        <Row align="middle" style={{ marginBottom: 12 }}>
          <Col span={6}>标题</Col>
          <Col span={18}><Input value={texts.loginTitle || ''} onChange={e => up('texts.loginTitle', e.target.value)} /></Col>
        </Row>
        <Row align="middle" style={{ marginBottom: 12 }}>
          <Col span={6}>副标题</Col>
          <Col span={18}><Input value={texts.loginSub || ''} onChange={e => up('texts.loginSub', e.target.value)} /></Col>
        </Row>
        <Row align="middle">
          <Col span={6}>登录按钮</Col>
          <Col span={18}><Input value={texts.loginBtn || ''} onChange={e => up('texts.loginBtn', e.target.value)} /></Col>
        </Row>
      </Card>

      <Card title="功能模块开关" size="small" style={{ marginBottom: 16 }}>
        {Object.keys(MODULE_LABELS).map(k => (
          <Row key={k} align="middle" style={{ marginBottom: 10 }}>
            <Col span={16}>{MODULE_LABELS[k]}</Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <Switch checked={modules[k] !== false} onChange={v => up('modules.' + k, v)} />
            </Col>
          </Row>
        ))}
      </Card>

      <Divider style={{ margin: '16px 0' }} />
      <Space>
        <Button type="primary" loading={saving} onClick={save}>保存配置</Button>
        <Button onClick={load}>重新加载</Button>
      </Space>
    </div>
  );
}
